import { useState, useRef, memo, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { SearchJudges } from '../../../../apollo/queries/SearchJudges.graphql';
import { GenderType } from '../../../../models';

// Counter for generating unique IDs (React 17 compatible alternative to useId)
let idCounter = 0;

// Parse a markdown table of judge assignments
// Handles any table format - finds Name column and Event column automatically
export function parseJudgeTable(text) {
  const lines = text.trim().split('\n');
  const assignments = [];

  // Find column indices from header row
  let nameCol = -1;
  let eventCol = -1;

  for (const line of lines) {
    if (!line.includes('|')) continue;
    if (/^[\s|:-]+$/.test(line)) continue;

    // Parse cells, preserving positions
    const cells = line.split('|').map((c) => c.trim());
    // Remove empty first/last from leading/trailing pipes
    while (cells.length > 0 && cells[0] === '') cells.shift();
    while (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();

    if (cells.length === 0) continue;

    // Try to find header row
    if (nameCol === -1) {
      const lower = cells.map((c) => c.toLowerCase());
      const ni = lower.indexOf('name');
      const ei = lower.indexOf('event');
      if (ni !== -1 && ei !== -1) {
        nameCol = ni;
        eventCol = ei;
        continue;
      }
      // No header yet - try to detect by finding event pattern
      for (let i = 0; i < cells.length; i++) {
        if (/^(VT|UB|BB|FX|PH|SR|PB|HB)\d?$/i.test(cells[i])) {
          eventCol = i;
          nameCol = 0;
          break;
        }
      }
    }

    // Parse data row
    if (
      nameCol >= 0 &&
      eventCol >= 0 &&
      cells.length > Math.max(nameCol, eventCol)
    ) {
      const name = cells[nameCol];
      const event = (cells[eventCol] || '').toUpperCase();

      if (!name || name.toLowerCase() === 'name') continue;

      const match = event.match(/^(VT|UB|BB|FX|PH|SR|PB|HB)(\d)?$/);
      if (match && name) {
        assignments.push({
          name,
          apparatus: match[1],
          judgeIndex: parseInt(match[2] || '1', 10) - 1,
        });
      }
    }
  }

  return assignments;
}

const JudgeSearchInput = memo(function JudgeSearchInput({
  gender,
  onSelect,
  excludeJudgeIds = [],
  placeholder = 'Search judge...',
  autoFocus = false,
  onBlur,
  onBulkPaste,
}) {
  // Generate unique ID once per component instance
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = `judge-search-${++idCounter}`;
  }
  const instanceId = instanceIdRef.current;
  const typeaheadRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState([]);

  // Focus on mount only once - don't re-focus on subsequent renders
  useEffect(() => {
    if (autoFocus && !hasFocusedRef.current) {
      hasFocusedRef.current = true;
      // Use requestAnimationFrame for more reliable timing than setTimeout
      requestAnimationFrame(() => {
        typeaheadRef.current?.focus();
      });
    }
  }, [autoFocus]);

  // Build gender-appropriate credential filter
  const mensFilter = {
    or: [{ mensBrevet: { eq: true } }, { mensOptional: { eq: true } }],
  };
  const womensFilter = {
    or: [
      { womensBrevet: { eq: true } },
      { womensNational: { eq: true } },
      { womensLevel10: { eq: true } },
    ],
  };

  const baseFilter = gender === GenderType.FEMALE ? womensFilter : mensFilter;
  const filter = searchTerm
    ? { ...baseFilter, name: { matchPhrasePrefix: searchTerm.trim() } }
    : baseFilter;

  const { data, loading } = useQuery(SearchJudges, {
    variables: {
      filter,
      limit: 25,
    },
    skip: !searchTerm,
  });

  // Filter out already-assigned judges - memoize to prevent unnecessary re-filtering
  const availableJudges = useMemo(
    () =>
      (data?.searchJudges?.items || []).filter(
        (judge) => !excludeJudgeIds.includes(judge.id)
      ),
    [data?.searchJudges?.items, excludeJudgeIds]
  );

  // Memoize change handler to prevent AsyncTypeahead re-renders
  const handleChange = useCallback(
    (selectedJudges) => {
      if (selectedJudges.length > 0) {
        onSelect(selectedJudges[0]);
        setSelected([]);
        setSearchTerm('');
      }
    },
    [onSelect]
  );

  return (
    <AsyncTypeahead
      ref={typeaheadRef}
      id={instanceId}
      clearButton
      filterBy={['name']}
      isLoading={loading}
      onChange={handleChange}
      onSearch={setSearchTerm}
      selected={selected}
      labelKey="name"
      placeholder={placeholder}
      options={availableJudges}
      onKeyDown={(e) => e.stopPropagation()}
      inputProps={{
        onPaste: (e) => {
          const text = e.clipboardData?.getData('text');
          // Always try to parse if there's a pipe character
          if (text && text.includes('|')) {
            const assignments = parseJudgeTable(text);
            if (assignments.length > 0 && onBulkPaste) {
              e.preventDefault();
              onBulkPaste(assignments);
            }
          }
        },
      }}
      onBlur={onBlur}
      size="sm"
      renderMenuItemChildren={(option) => {
        const creds = [
          option.womensBrevet && 'WB',
          option.womensNational && 'WN',
          option.womensLevel10 && 'W10',
          option.mensBrevet && 'MB',
          option.mensOptional && 'MO',
          option.mensCompulsory && 'MC',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <span>
            {option.name}
            {creds && (
              <small style={{ color: '#888', marginLeft: 6 }}>{creds}</small>
            )}
          </span>
        );
      }}
    />
  );
});

export default JudgeSearchInput;
